import { FC, memo, useMemo } from 'react';
import { Flex } from 'antd';
import { CellContext, createColumnHelper } from '@tanstack/react-table';
import * as Icon from 'react-feather';
import { expand } from 'uri-template-lite';
import {
  DirectoryTableProps,
  formatDate,
  formatDateTimeShort,
  getGroupDescriptionRef,
  getMapBy,
  getUserName,
  OUTPUT_DATA_EVENT_LEVEL_CODES,
  OUTPUT_DATA_PROCESSING_TYPE_CODES,
  OutputProcessingErrorExcelUrl,
  OutputProcessingEventReportType,
  ProcessingEventDirectoryEntryType,
  Table,
  TableOperationButton,
  Tooltip,
  useSystemClassifierSelectOptions,
} from '@atlas-frontend/sdk';
import { AppLink } from '../../../common/AppLink';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { ErrorLevel } from '../ErrorLevel';
import { VersionCell } from '../VersionCell';
import UriTemplate from 'uri-template-lite';
import { path } from '../../../paths';
import translations from './OutputDataProcessingEventsTable.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory(
  'output-data-processing-events-table',
);
addTranslations(translations);

const columnHelper = createColumnHelper<ProcessingEventDirectoryEntryType>();

export type OutputDataProcessingEventsTableProps =
  DirectoryTableProps<ProcessingEventDirectoryEntryType>;

export const OutputDataProcessingEventsTable: FC<
  OutputDataProcessingEventsTableProps
> = (props) => {
  const { t } = useTranslation();

  const {
    PROCESSING_TYPE: processingTypeOptions,
    PROCESSING_ERROR_LEVEL: errorLevelOptions,
    PROCESSING_STATUS: statusOptions,
    OUTPUT_TYPE: outputTypeOptions,
    PROC_VAL_ERROR_LEVEL: validationErrorLevels,
  } = useSystemClassifierSelectOptions(
    [
      'PROCESSING_TYPE',
      'PROCESSING_ERROR_LEVEL',
      'PROCESSING_STATUS',
      'OUTPUT_TYPE',
      'PROC_VAL_ERROR_LEVEL',
    ],
    ({ id, name, code }) => ({ id, name, code }),
  );

  const filterOptions = useMemo(() => {
    const forTable = {
      outputDataObjectGroupType: [
        {
          id: 'MIXED',
          name: t('table.outputDataObjectGroupType.MIXED'),
        },
        ...outputTypeOptions.map(({ name }) => ({ id: name, name })),
      ],
      processingTypeId: processingTypeOptions.filter(({ code }) =>
        OUTPUT_DATA_PROCESSING_TYPE_CODES.includes(code),
      ),
      errorLevelId: errorLevelOptions.filter(({ code }) =>
        OUTPUT_DATA_EVENT_LEVEL_CODES.includes(code),
      ),
      statusId: statusOptions,
    };

    return {
      forTable,
      byId: {
        processingTypeId: getMapBy(forTable.processingTypeId, 'id'),
        errorLevelId: getMapBy(forTable.errorLevelId, 'id'),
        statusId: getMapBy(forTable.statusId, 'id'),
      },
    };
  }, [
    t,
    outputTypeOptions,
    processingTypeOptions,
    errorLevelOptions,
    statusOptions,
  ]);

  const columns = useMemo(() => {
    return [
      columnHelper.accessor('outputDataObjectDescription', {
        id: 'outputDataObjectDescription',
        header: t('header.outputDataObjectDescription'),
        meta: {
          className: 'sticky-left',
          width: '20ch',
        },
      }),
      columnHelper.accessor('outputDataObjectDescriptionParent', {
        id: 'outputDataObjectDescriptionParent',
        header: t('header.outputDataObjectDescriptionParent'),
        meta: { width: '30ch' },
      }),
      columnHelper.accessor(
        ({ outputDataObjectGroupType }) =>
          outputDataObjectGroupType === 'MIXED'
            ? t('table.outputDataObjectGroupType.MIXED')
            : outputDataObjectGroupType,
        {
          id: 'outputDataObjectGroupType',
          header: t('header.outputDataObjectGroupType'),
          filterFn: 'includesOption',
          meta: { width: '20ch' },
        },
      ),
      columnHelper.accessor(
        ({ processingTypeId }) =>
          filterOptions.byId.processingTypeId.get(processingTypeId)?.name || '',
        {
          id: 'processingTypeId',
          header: t('header.processingTypeId'),
          filterFn: 'includesOption',
          meta: { width: '22ch' },
        },
      ),
      columnHelper.accessor(({ valueDate }) => formatDate(valueDate), {
        id: 'valueDate',
        header: t('header.valueDate'),
        filterFn: 'valueDateSelect',
        meta: { width: '18ch' },
      }),
      columnHelper.accessor(({ startTime }) => formatDateTimeShort(startTime), {
        id: 'startTime',
        header: t('header.startTime'),
        filterFn: 'validPeriod',
        meta: { width: '21ch' },
      }),
      columnHelper.accessor(({ endTime }) => formatDateTimeShort(endTime), {
        id: 'endTime',
        header: t('header.endTime'),
        filterFn: 'validPeriod',
        meta: { width: '21ch' },
      }),
      columnHelper.accessor(({ createdBy }) => createdBy.username, {
        id: 'createdBy',
        header: t('header.createdBy'),
        cell: CreatedByCell,
        meta: { width: '15ch' },
      }),
      columnHelper.display({
        id: 'errorLevel',
        header: t('header.errorLevel'),
        cell: (props) => (
          <ErrorLevel
            type="processingEvent"
            typeId={props.row.original.id}
            validationErrorLevels={getMapBy(validationErrorLevels, 'id')}
            mode="compact"
            outputProcessingDataObjectId={
              props.row.original.outputProcessingDataObjectId
            }
            calculationErrors={props.row.original.outputCalculationErrors}
            missingSourceData={
              props.row.original.outputVersionMissingSourceData
            }
            versionValidationErrorLevelId={
              props.row.original.outputVersionValidationErrorLevelId
            }
            hasAccess={props.row.original.hasAccess}
          />
        ),
        enableColumnFilter: false,
        enableSorting: false,
        meta: {
          width: '10ch',
        },
      }),
      columnHelper.accessor(
        ({ outputVersion, outputVersionStatus }) => ({
          version: outputVersion,
          versionStatus: outputVersionStatus,
        }),
        {
          id: 'version',
          header: t('header.version'),
          cell: (props) => <VersionCell {...props.getValue()} />,
          enableColumnFilter: false,
          enableSorting: false,
          meta: {
            width: '20ch',
          },
        },
      ),
      columnHelper.display({
        id: 'operation',
        header: t('header.operations'),
        cell: (props) => <OperationsCell {...props} />,
        meta: { width: '9ch' },
        enableColumnFilter: false,
      }),
    ];
  }, [t, filterOptions, validationErrorLevels]);

  return (
    <Table
      name="processing-errors-directory-table"
      columns={columns}
      hideClearFilters
      manualFiltering
      manualSorting
      options={filterOptions.forTable}
      scrollContainerSelector="#processing-errors-directory-content"
      sticky
      virtual
      rowKey="outputProcessingDataObjectId"
      {...props}
    />
  );
};

const CreatedByCell: FC<CellContext<ProcessingEventDirectoryEntryType, any>> =
  memo((props) => {
    const { createdBy } = props.row.original;
    return (
      <Tooltip title={getUserName(createdBy)}>{createdBy.username}</Tooltip>
    );
  });

const OperationsCell: FC<CellContext<ProcessingEventDirectoryEntryType, any>> =
  memo((props) => {
    const { t } = useTranslation();
    const { row } = props;
    const {
      id,
      outputProcessingDataObjectId,
      valueDate,
      dataObjectGroupId,
      outputVersionId,
      outputDataObjectDescriptionId,
      hasAccess,
    } = row.original;
    const groupDescriptionRef = getGroupDescriptionRef(
      dataObjectGroupId,
      outputDataObjectDescriptionId || undefined,
    );

    const linkTo = new UriTemplate(
      `${path.visualValidationCrossTable}{?groupDescriptionRef,valueDate,version}`,
    ).expand({
      groupDescriptionRef,
      valueDate,
      version: outputVersionId,
    });

    return (
      <Flex gap="small" justify="space-between">
        <Flex gap="small">
          <TableOperationButton
            id={`download-${id}`}
            title={hasAccess ? t('button.download') : t('button.no-access')}
            icon={<Icon.Download size={18} />}
            href={expand(OutputProcessingErrorExcelUrl, {
              id,
              type: OutputProcessingEventReportType.processingEvent,
              processingDataObjectId: outputProcessingDataObjectId,
            })}
            disabled={!hasAccess}
          />
          <AppLink id={`open-${id}`} to={linkTo} target="_blank">
            {({ type, ...props }) => (
              <TableOperationButton
                {...props}
                title={
                  hasAccess ? t('button.open-data') : t('button.no-access')
                }
                icon={<Icon.FileText size={18} />}
                disabled={!hasAccess}
              />
            )}
          </AppLink>
        </Flex>
      </Flex>
    );
  });
